import React, { useState } from 'react';
import { useLanguage } from '../context/AppContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Shield, FileText, Scale, ChevronLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

export const LegalPage = () => {
  const { language } = useLanguage();
  const fr = language === 'fr';

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Link to="/login" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-6">
          <ChevronLeft className="w-4 h-4" /> {fr ? 'Retour' : 'Back'}
        </Link>

        <h1 className="text-3xl font-heading font-bold text-slate-900 mb-2" data-testid="legal-title">
          {fr ? 'Mentions legales' : 'Legal'}
        </h1>
        <p className="text-slate-500 text-sm mb-8">JoSmart Uni — JoSmart Technologies</p>

        <Tabs defaultValue="privacy">
          <TabsList className="mb-6">
            <TabsTrigger value="privacy" className="gap-1.5" data-testid="tab-privacy">
              <Shield className="w-4 h-4" />{fr ? 'Confidentialite' : 'Privacy'}
            </TabsTrigger>
            <TabsTrigger value="terms" className="gap-1.5" data-testid="tab-terms">
              <FileText className="w-4 h-4" />{fr ? "Conditions d'utilisation" : 'Terms'}
            </TabsTrigger>
            <TabsTrigger value="rules" className="gap-1.5" data-testid="tab-rules">
              <Scale className="w-4 h-4" />{fr ? 'Reglement' : 'Rules'}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="privacy">
            <LegalCard>
              <h2>Politique de Confidentialite</h2>
              <p className="subtitle">Derniere mise a jour : Aout 2026</p>

              <h3>1. Responsable du traitement</h3>
              <p>JoSmart Technologies, editeur de la plateforme JoSmart Uni, est responsable du traitement des donnees personnelles collectees via la plateforme. Siege social : Kinshasa, Republique Democratique du Congo.</p>

              <h3>2. Donnees collectees</h3>
              <p>Nous collectons les donnees suivantes dans le cadre de la gestion academique :</p>
              <ul>
                <li><strong>Donnees d'identification :</strong> nom, prenom, adresse e-mail, numero de telephone, photo de profil.</li>
                <li><strong>Donnees academiques :</strong> matricule, faculte, departement, promotion, notes, credits, deliberations, certificats.</li>
                <li><strong>Donnees de connexion :</strong> adresse IP, type de navigateur, date et heure de connexion, journaux d'activite.</li>
                <li><strong>Donnees financieres :</strong> historique des paiements de frais academiques (aucune donnee bancaire ou Mobile Money n'est stockee directement).</li>
                <li><strong>Donnees de transport :</strong> localisation GPS des bus universitaires (uniquement pendant le trajet).</li>
              </ul>

              <h3>3. Finalites du traitement</h3>
              <p>Les donnees sont traitees exclusivement pour :</p>
              <ul>
                <li>La gestion administrative et academique des universites.</li>
                <li>L'inscription, le suivi des notes et la delivrance des documents officiels.</li>
                <li>La communication entre etudiants, enseignants et administration.</li>
                <li>Le suivi des paiements des frais academiques.</li>
                <li>Le fonctionnement du module de transport universitaire.</li>
                <li>L'amelioration de la plateforme et l'assistance technique.</li>
              </ul>

              <h3>4. Base legale</h3>
              <p>Le traitement est fonde sur :</p>
              <ul>
                <li>Le consentement de l'utilisateur lors de la creation du compte.</li>
                <li>L'execution du contrat de service entre l'universite et JoSmart Technologies.</li>
                <li>Les obligations legales applicables en RDC en matiere d'enseignement superieur.</li>
              </ul>

              <h3>5. Partage des donnees</h3>
              <p>Les donnees personnelles ne sont jamais vendues a des tiers. Elles peuvent etre partagees avec :</p>
              <ul>
                <li>L'universite d'appartenance de l'utilisateur (dans le cadre strict de la gestion academique).</li>
                <li>Le Ministere de l'Enseignement Superieur et Universitaire (ESU) de la RDC, dans le cadre des obligations reglementaires.</li>
                <li>Les prestataires techniques (hebergement, base de donnees) sous contrat de confidentialite.</li>
              </ul>

              <h3>6. Securite des donnees</h3>
              <p>JoSmart Technologies met en oeuvre les mesures suivantes :</p>
              <ul>
                <li>Chiffrement des mots de passe (bcrypt).</li>
                <li>Authentification par jeton JWT avec expiration.</li>
                <li>Isolation stricte des donnees entre universites (architecture multi-tenant).</li>
                <li>Controle d'acces base sur 28+ roles avec permissions granulaires.</li>
                <li>Communications chiffrees (HTTPS/TLS).</li>
              </ul>

              <h3>7. Conservation des donnees</h3>
              <p>Les donnees sont conservees pendant toute la duree de la relation contractuelle avec l'universite. Les donnees des etudiants diplomes sont archivees conformement a la reglementation congolaise sur l'enseignement superieur (minimum 10 ans). L'utilisateur peut demander la suppression de ses donnees personnelles, sous reserve des obligations legales de conservation.</p>

              <h3>8. Droits des utilisateurs</h3>
              <p>Conformement a la legislation applicable, vous disposez des droits suivants :</p>
              <ul>
                <li><strong>Droit d'acces :</strong> obtenir une copie de vos donnees personnelles.</li>
                <li><strong>Droit de rectification :</strong> corriger des donnees inexactes.</li>
                <li><strong>Droit de suppression :</strong> demander l'effacement de vos donnees (sous reserve des obligations legales).</li>
                <li><strong>Droit d'opposition :</strong> vous opposer a certains traitements.</li>
              </ul>
              <p>Pour exercer ces droits, contactez :</p>
              <ul>
                <li><strong>Email :</strong> support.josmarttech@gmail.com</li>
                <li><strong>Telephone :</strong> +1 (431) 334-5083</li>
              </ul>

              <h3>9. Cookies et traceurs</h3>
              <p>La plateforme utilise des cookies techniques strictement necessaires au fonctionnement (authentification, preferences de langue). Aucun cookie publicitaire ou de profilage n'est utilise.</p>

              <h3>10. Modifications</h3>
              <p>Cette politique peut etre mise a jour. Les utilisateurs seront informes de tout changement significatif via une notification dans la plateforme.</p>
            </LegalCard>
          </TabsContent>

          <TabsContent value="terms">
            <LegalCard>
              <h2>Conditions Generales d'Utilisation</h2>
              <p className="subtitle">Derniere mise a jour : Aout 2026</p>

              <h3>1. Objet</h3>
              <p>Les presentes conditions generales d'utilisation (CGU) regissent l'acces et l'utilisation de la plateforme JoSmart Uni, editee par JoSmart Technologies, destinee a la gestion academique des etablissements d'enseignement superieur et universitaire en Republique Democratique du Congo.</p>

              <h3>2. Acceptation</h3>
              <p>L'utilisation de la plateforme implique l'acceptation pleine et entiere des presentes CGU. Si vous n'acceptez pas ces conditions, vous ne devez pas utiliser la plateforme.</p>

              <h3>3. Inscription et comptes</h3>
              <ul>
                <li>L'inscription est reservee aux membres des universites partenaires (etudiants, enseignants, personnel administratif).</li>
                <li>Les etudiants s'inscrivent via le code universite et le code promotion fournis par leur etablissement.</li>
                <li>Les comptes du personnel sont crees par l'administration de l'universite ou le Super Administrateur.</li>
                <li>Chaque utilisateur est responsable de la confidentialite de ses identifiants de connexion.</li>
                <li>Toute utilisation non autorisee d'un compte doit etre signalee immediatement.</li>
              </ul>

              <h3>4. Services fournis</h3>
              <p>JoSmart Uni propose les services suivants :</p>
              <ul>
                <li>Gestion des inscriptions et du parcours academique.</li>
                <li>Consultation et saisie des notes (systeme LMD).</li>
                <li>Gestion des cours, devoirs et travaux pratiques.</li>
                <li>Messagerie interne et notifications.</li>
                <li>Gestion des paiements des frais academiques.</li>
                <li>Module de transport universitaire avec suivi GPS.</li>
                <li>Deliberations et generation de documents officiels.</li>
                <li>Assistant IA pour l'aide academique.</li>
                <li>Bibliotheque numerique et dictionnaire.</li>
              </ul>

              <h3>5. Obligations de l'utilisateur</h3>
              <p>L'utilisateur s'engage a :</p>
              <ul>
                <li>Fournir des informations exactes et a jour lors de l'inscription.</li>
                <li>Utiliser la plateforme uniquement a des fins academiques et educatives.</li>
                <li>Ne pas tenter d'acceder aux donnees d'autres universites ou utilisateurs.</li>
                <li>Ne pas modifier, copier ou distribuer le contenu de la plateforme sans autorisation.</li>
                <li>Respecter les droits de propriete intellectuelle.</li>
                <li>Ne pas utiliser la plateforme pour diffuser du contenu illegal, offensant ou discriminatoire.</li>
                <li>Ne pas tenter de perturber le fonctionnement de la plateforme.</li>
              </ul>

              <h3>6. Propriete intellectuelle</h3>
              <p>La plateforme JoSmart Uni, son code source, son design, ses logos et son contenu sont la propriete exclusive de JoSmart Technologies. Toute reproduction, meme partielle, est interdite sans autorisation ecrite prealable.</p>

              <h3>7. Responsabilite</h3>
              <ul>
                <li>JoSmart Technologies s'efforce d'assurer la disponibilite et le bon fonctionnement de la plateforme, sans garantie d'absence d'interruption.</li>
                <li>JoSmart Technologies ne saurait etre tenue responsable des dommages indirects lies a l'utilisation de la plateforme.</li>
                <li>Les universites sont responsables de l'exactitude des donnees academiques qu'elles saisissent.</li>
                <li>En cas de force majeure (coupure d'electricite, panne internet, catastrophe naturelle), JoSmart Technologies est degage de toute responsabilite.</li>
              </ul>

              <h3>8. Suspension et resiliation</h3>
              <p>JoSmart Technologies se reserve le droit de suspendre ou supprimer un compte en cas de :</p>
              <ul>
                <li>Violation des presentes CGU.</li>
                <li>Utilisation frauduleuse de la plateforme.</li>
                <li>Demande de l'universite d'appartenance.</li>
                <li>Inactivite prolongee (plus de 24 mois).</li>
              </ul>

              <h3>9. Disponibilite</h3>
              <p>La plateforme est accessible 24h/24, 7j/7, sous reserve des periodes de maintenance. Le mode hors-ligne (PWA) permet un acces limite aux donnees synchronisees en cas d'absence de connexion internet.</p>

              <h3>10. Droit applicable</h3>
              <p>Les presentes CGU sont regies par le droit de la Republique Democratique du Congo. Tout litige sera soumis aux juridictions competentes de Kinshasa.</p>

              <h3>11. Contact</h3>
              <p>Pour toute question relative aux presentes conditions :</p>
              <ul>
                <li><strong>Email :</strong> support.josmarttech@gmail.com</li>
                <li><strong>Telephone :</strong> +1 (431) 334-5083</li>
              </ul>
            </LegalCard>
          </TabsContent>

          <TabsContent value="rules">
            <LegalCard>
              <h2>Reglement Interieur de la Plateforme</h2>
              <p className="subtitle">Derniere mise a jour : Aout 2026</p>

              <h3>1. Objet du reglement</h3>
              <p>Le present reglement definit les regles de conduite et d'utilisation de la plateforme JoSmart Uni par tous les utilisateurs : etudiants, enseignants, personnel administratif et technique.</p>

              <h3>2. Regles generales de conduite</h3>
              <ul>
                <li><strong>Respect mutuel :</strong> tout echange via la messagerie ou les forums doit rester courtois et professionnel.</li>
                <li><strong>Interdiction de harcelement :</strong> tout comportement de harcelement, intimidation ou discrimination est strictement interdit et passible de sanctions.</li>
                <li><strong>Integrite academique :</strong> la fraude, le plagiat et la falsification de documents sont interdits et seront signales a l'universite concernee.</li>
                <li><strong>Confidentialite :</strong> les utilisateurs ne doivent pas divulguer les informations personnelles d'autres utilisateurs obtenues via la plateforme.</li>
              </ul>

              <h3>3. Utilisation des fonctionnalites</h3>
              <h4>3.1 Messagerie</h4>
              <ul>
                <li>La messagerie est reservee aux communications academiques et administratives.</li>
                <li>L'envoi de spam, publicite ou contenu sans rapport avec l'education est interdit.</li>
              </ul>

              <h4>3.2 Notes et evaluations</h4>
              <ul>
                <li>Seuls les enseignants autorises peuvent saisir et modifier les notes.</li>
                <li>Les notes verrouillees apres deliberation ne peuvent plus etre modifiees sans autorisation du Recteur ou du SG Academique.</li>
                <li>Toute tentative de modification non autorisee des notes sera consideree comme une fraude.</li>
              </ul>

              <h4>3.3 Paiements</h4>
              <ul>
                <li>Les paiements effectues via la plateforme sont definitifs apres confirmation.</li>
                <li>Les recus de paiement sont generes automatiquement et font foi.</li>
                <li>Toute contestation doit etre adressee au service financier de l'universite.</li>
              </ul>

              <h4>3.4 Transport universitaire</h4>
              <ul>
                <li>Le suivi GPS des bus est active uniquement pendant les heures de service.</li>
                <li>Les donnees de localisation ne sont pas conservees au-dela de 24 heures.</li>
                <li>Les chauffeurs doivent activer le partage de position pendant leur service.</li>
              </ul>

              <h4>3.5 Assistant IA</h4>
              <ul>
                <li>L'assistant IA est un outil d'aide a l'apprentissage et ne remplace pas l'enseignement.</li>
                <li>Les reponses generees par l'IA doivent etre verifiees par l'utilisateur.</li>
                <li>L'utilisation de l'IA pour generer des travaux a soumettre comme etant les siens constitue une fraude academique.</li>
              </ul>

              <h3>4. Roles et responsabilites</h3>
              <h4>4.1 Super Administrateur</h4>
              <p>Gere l'ensemble de la plateforme, cree les universites, nomme les co-administrateurs et les recteurs.</p>

              <h4>4.2 Recteur / Administration universitaire</h4>
              <p>Gere son universite, valide les inscriptions, supervise les deliberations et les notes.</p>

              <h4>4.3 Enseignants</h4>
              <p>Saisissent les notes, gerent les cours et devoirs, respectent les delais de saisie des notes.</p>

              <h4>4.4 Etudiants</h4>
              <p>Consultent leurs notes, cours et emplois du temps. Soumettent leurs devoirs dans les delais. Respectent le reglement de leur universite.</p>

              <h3>5. Sanctions</h3>
              <p>En cas de non-respect du present reglement :</p>
              <ul>
                <li><strong>Avertissement :</strong> premier manquement mineur.</li>
                <li><strong>Suspension temporaire :</strong> manquement repete ou grave.</li>
                <li><strong>Suppression du compte :</strong> fraude, harcelement ou violation grave.</li>
                <li><strong>Signalement a l'universite :</strong> pour toute infraction relevant de la discipline academique.</li>
              </ul>

              <h3>6. Signalement</h3>
              <p>Tout utilisateur peut signaler un comportement contraire au present reglement via la messagerie interne ou par :</p>
              <ul>
                <li><strong>Email :</strong> support.josmarttech@gmail.com</li>
                <li><strong>Telephone :</strong> +1 (431) 334-5083</li>
              </ul>
              <p>Les signalements sont traites de maniere confidentielle.</p>

              <h3>7. Modifications</h3>
              <p>JoSmart Technologies se reserve le droit de modifier le present reglement. Les utilisateurs seront informes de toute modification via une notification.</p>
            </LegalCard>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

const LegalCard = ({ children }) => (
  <div className="bg-white rounded-2xl border border-slate-100 p-6 sm:p-8 prose prose-slate prose-sm max-w-none
    [&_h2]:text-xl [&_h2]:font-heading [&_h2]:font-bold [&_h2]:text-slate-900 [&_h2]:mb-1
    [&_h3]:text-base [&_h3]:font-heading [&_h3]:font-semibold [&_h3]:text-slate-800 [&_h3]:mt-6 [&_h3]:mb-2
    [&_h4]:text-sm [&_h4]:font-semibold [&_h4]:text-slate-700 [&_h4]:mt-4 [&_h4]:mb-1
    [&_p]:text-slate-600 [&_p]:text-sm [&_p]:leading-relaxed
    [&_.subtitle]:text-xs [&_.subtitle]:text-slate-400 [&_.subtitle]:mb-6
    [&_ul]:text-sm [&_ul]:text-slate-600 [&_ul]:space-y-1.5 [&_ul]:pl-5 [&_ul]:list-disc
    [&_li]:leading-relaxed
    [&_strong]:text-slate-800
  ">
    {children}
  </div>
);

export default LegalPage;
